import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Avatar} from 'src/design/atoms/Avatar';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDebouncedValue, useDisclosure} from '@mantine/hooks';
import {IconCalendarStats, IconSearch} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useCallback, useMemo, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {InspectionStateEnum} from 'src/api/ProactApiClient';
import type {InspectionSimpleResponse} from 'src/api/ProactApiClient';
import {CompletionProgress} from 'src/design/atoms/CompletionProgress';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Pill} from 'src/design/atoms/Pill';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {SelectBox} from 'src/design/atoms/SelectBox';
import {Textbox} from 'src/design/atoms/Textbox';
import {Paginator} from 'src/design/organisms/Pagination';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {InspectionStateDataOptions} from 'src/shared/constants/columnFilterData';
import {InspectionStateEnumPill} from 'src/shared/constants/PillConstants';
import {useGetClientSelectOptionsQuery} from 'src/redux/queries/client';
import {
  useDeleteInspectionMutation,
  useGetInspectionAssigneeCandidatesQuery,
  useGetInspectionListV2Query,
} from 'src/redux/queries/inspection';
import {useGetInspectionScheduleQuery} from 'src/redux/queries/inspectionSchedule';
import {useGetInspectionTypeSelectOptionsQuery} from 'src/redux/queries/inspectionType';
import {cadenceLabel} from 'src/app/inspections/schedules/inspectionScheduleHelpers';
import {useGetSiteSelectOptionsQuery} from 'src/redux/queries/site';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {isInspectionEditableState} from 'src/pages/Modules/Inspections/shared/inspectionHelpers';
import {useAlert} from 'src/shared/hooks/useAlert';
import {
  formatUtcDateTime,
  formatUtcShortDate,
} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import type {TimelineMode} from '../inspectionTimelineGrouping';
import {TIMELINE_MODE_OPTIONS} from '../inspectionTimelineGrouping';
import {InspectionDetailDrawer} from './InspectionDetailDrawer';
import {InspectionTimelineList} from './InspectionTimelineList';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const EMPTY_SELECT_QUERY = {
  start: 0,
  length: 10000,
  filters: [],
  filterFns: [],
  sorting: [],
};

const TIMELINE_PAGE_LENGTH = 500;

type ManageViewMode = 'table' | TimelineMode;

const VIEW_MODE_OPTIONS = [
  {label: 'Table', value: 'table'},
  ...TIMELINE_MODE_OPTIONS,
];

const PASS_FAIL_OPTIONS = [
  {id: 'all', label: 'All'},
  {id: 'true', label: 'Has failures'},
  {id: 'false', label: 'No failures'},
];

// Scope is server-enforced — `manageView: true` just tells the backend which arm to apply.
export const InspectionManageTab: React.FC = () => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const scheduleParam = Number(searchParams.get('scheduleId'));
  const inspectionScheduleId =
    Number.isFinite(scheduleParam) && scheduleParam > 0
      ? scheduleParam
      : undefined;
  const perms = useInspectionPermissions();
  const canManage = perms.manage || perms.manageAll;
  const [detailTarget, setDetailTarget] = useState<InspectionSimpleResponse>();
  const [detailOpened, detailHandlers] = useDisclosure(false);
  const [deleteTarget, setDeleteTarget] = useState<InspectionSimpleResponse>();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteInspection] = useDeleteInspectionMutation();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  const [buildingInput, setBuildingInput] = useState('');
  const [debouncedBuilding] = useDebouncedValue(buildingInput, 300);

  const [passFail, setPassFail] = useState('all');

  const [viewMode, setViewMode] = useState<ManageViewMode>('table');
  const isTimeline = viewMode !== 'table';

  // Timeline modes lift the MRT column filters into toolbar selects.
  const [tlSite, setTlSite] = useState('');
  const [tlClient, setTlClient] = useState('');
  const [tlType, setTlType] = useState('');
  const [tlState, setTlState] = useState('');
  const [tlAssignee, setTlAssignee] = useState('');

  const {data: sites} = useGetSiteSelectOptionsQuery(EMPTY_SELECT_QUERY);
  const {data: clients} = useGetClientSelectOptionsQuery(EMPTY_SELECT_QUERY);
  const {data: inspectionTypes} =
    useGetInspectionTypeSelectOptionsQuery(EMPTY_SELECT_QUERY);

  const siteFilterData = useMemo(
    () =>
      (sites?.data ?? []).map(o => ({
        value: String(o.id),
        label: o.label ?? String(o.id),
      })),
    [sites]
  );
  const clientFilterData = useMemo(
    () =>
      (clients?.data ?? []).map(o => ({
        value: String(o.id),
        label: o.label ?? String(o.id),
      })),
    [clients]
  );
  const typeFilterData = useMemo(
    () =>
      (inspectionTypes?.data ?? []).map(o => ({
        value: String(o.id),
        label: o.label ?? String(o.id),
      })),
    [inspectionTypes]
  );

  const openDetailFor = useCallback(
    (row: InspectionSimpleResponse) => {
      setDetailTarget(row);
      detailHandlers.open();
    },
    [detailHandlers]
  );

  const {data: filterSchedule} = useGetInspectionScheduleQuery(
    inspectionScheduleId as number,
    {skip: inspectionScheduleId === undefined}
  );

  const scheduleFilterOptions = useMemo(
    () =>
      inspectionScheduleId === undefined
        ? []
        : [
            {
              id: String(inspectionScheduleId),
              label: filterSchedule
                ? `Schedule: ${cadenceLabel(filterSchedule)} · ${filterSchedule.siteName}`
                : 'Schedule',
            },
          ],
    [inspectionScheduleId, filterSchedule]
  );

  const clearScheduleFilter = useCallback(() => {
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete('scheduleId');
        return next;
      },
      {replace: true}
    );
  }, [setSearchParams]);

  const scheduleFilterBox =
    inspectionScheduleId === undefined ? null : (
      <SelectBox
        ariaLabel="Schedule"
        placeholder="Schedule"
        options={scheduleFilterOptions}
        value={String(inspectionScheduleId)}
        onSelectChangeCallback={value => {
          if (!value) clearScheduleFilter();
        }}
        wrapperProps={{w: 220}}
      />
    );

  const handleDelete = async () => {
    if (!deleteTarget) return;

    confirmDeleteHandlers.close();
    setIsDeleting(true);

    try {
      await deleteInspection(deleteTarget.id).unwrap();
      setAlert('Inspection was successfully deleted.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsDeleting(false);
    setDeleteTarget(undefined);
  };

  const columns = useMemo<MRT_ColumnDef<InspectionSimpleResponse>[]>(
    () => [
      {
        id: 'siteId',
        accessorFn: row => String(row.siteId),
        header: 'Site',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {data: siteFilterData},
        Cell: ({row}) => (
          <div>
            <Text size="sm" fw={600}>
              {row.original.siteName}
            </Text>
            {row.original.siteNumber && (
              <Text size="xs" c="dimmed">
                #{row.original.siteNumber}
              </Text>
            )}
          </div>
        ),
      },
      {
        id: 'clientId',
        accessorFn: row => (row.clientId ? String(row.clientId) : ''),
        header: 'Client',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {data: clientFilterData},
        Cell: ({row}) => row.original.clientName ?? '—',
      },
      {
        id: 'inspectionTypeId',
        accessorFn: row => String(row.inspectionTypeId),
        header: 'Type',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {data: typeFilterData},
        Cell: ({row}) => row.original.inspectionTypeName,
      },
      {
        id: 'scheduledStartAt',
        accessorFn: row =>
          row.scheduledStartAt ? parseUtcDate(row.scheduledStartAt) : undefined,
        header: 'Schedule Date',
        enableColumnFilter: false,
        Cell: ({cell}) => formatUtcDateTime(cell.getValue<Date | undefined>()),
      },
      {
        id: 'dueAt',
        accessorFn: row => (row.dueAt ? parseUtcDate(row.dueAt) : undefined),
        header: 'Due Date',
        filterVariant: 'date',
        Cell: ({cell}) => formatUtcShortDate(cell.getValue<Date | undefined>()),
      },
      {
        id: 'state',
        accessorFn: row => String(row.state),
        header: 'Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {data: InspectionStateDataOptions},
        size: 160,
        minSize: 140,
        Cell: ({row}) => (
          <Pill {...InspectionStateEnumPill(row.original.state)} />
        ),
      },
      {
        id: 'assigneeId',
        accessorFn: row => (row.assigneeId ? String(row.assigneeId) : ''),
        header: 'Assignee',
        enableColumnFilterModes: false,
        Cell: ({row}) => {
          if (row.original.assigneeName) {
            return (
              <Group gap="xs" wrap="nowrap">
                <Avatar
                  size="sm"
                  radius="xl"
                  name={row.original.assigneeName}
                  color="initials"
                />
                <Text size="sm">{row.original.assigneeName}</Text>
              </Group>
            );
          }

          const canAssign =
            (perms.manage || perms.manageAll) &&
            row.original.state !== InspectionStateEnum.Completed;

          if (canAssign) {
            return (
              <Group gap="xs" wrap="nowrap">
                <Anchor
                  size="sm"
                  fw={600}
                  c="blue"
                  onClick={() => openDetailFor(row.original)}>
                  ● Unassigned · assign
                </Anchor>
              </Group>
            );
          }

          return (
            <Text size="sm" c="dimmed">
              Unassigned
            </Text>
          );
        },
      },
      {
        id: 'devices',
        header: 'Devices',
        enableColumnFilter: false,
        enableSorting: false,
        mantineTableHeadCellProps: {style: {textAlign: 'right'}},
        mantineTableBodyCellProps: {style: {textAlign: 'right'}},
        accessorFn: row => row.scopedDeviceCount,
      },
      {
        id: 'progress',
        header: 'Progress',
        enableColumnFilter: false,
        accessorFn: row =>
          row.scopedDeviceCount > 0
            ? Math.round(
                (row.terminalDeviceCount / row.scopedDeviceCount) * 100
              )
            : 0,
        Cell: ({row}) => (
          <CompletionProgress
            completed={row.original.terminalDeviceCount}
            total={row.original.scopedDeviceCount}
          />
        ),
      },
    ],
    [
      siteFilterData,
      clientFilterData,
      typeFilterData,
      perms.manage,
      perms.manageAll,
      openDetailFor,
    ]
  );

  const tableState = useTableState({
    identifier: 'inspection-manage-v2',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
    defaultSorting: [{id: 'dueAt', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {
    start,
    length,
    filters,
    filterFns,
    sorting,
    pagination,
    setPagination,
  } = tableState;

  const hasFailures = passFail === 'all' ? undefined : passFail === 'true';

  const {data, isLoading, isError, isFetching, refetch} =
    useGetInspectionListV2Query(
      {
        start,
        length,
        filters,
        filterFns,
        sorting,
        manageView: true,
        assignedToAnyone: true,
        hasFailures,
        building: debouncedBuilding || undefined,
        search: debouncedSearch || undefined,
        inspectionScheduleId,
      },
      {skip: isTimeline}
    );
  const rows = useMemo(() => data?.data ?? [], [data]);
  const totalCount = data?.totalRecords ?? 0;
  const unassignedCount = data?.unassignedCount ?? 0;

  const {data: assigneeCandidates} = useGetInspectionAssigneeCandidatesQuery(
    undefined,
    {skip: !isTimeline}
  );
  const assigneeFilterData = useMemo(
    () =>
      (assigneeCandidates ?? []).map(c => ({
        id: String(c.userId),
        label: c.name,
      })),
    [assigneeCandidates]
  );

  const {data: timelineData, isLoading: isTimelineLoading} =
    useGetInspectionListV2Query(
      {
        start: 0,
        length: TIMELINE_PAGE_LENGTH,
        filters: [],
        filterFns: [],
        sorting: [{id: 'scheduledStartAt', desc: false}],
        manageView: true,
        assignedToAnyone: true,
        siteId: tlSite ? Number(tlSite) : undefined,
        clientId: tlClient ? Number(tlClient) : undefined,
        inspectionTypeId: tlType ? Number(tlType) : undefined,
        states: tlState ? [Number(tlState) as InspectionStateEnum] : undefined,
        assigneeId: tlAssignee ? Number(tlAssignee) : undefined,
        search: debouncedSearch || undefined,
        inspectionScheduleId,
      },
      {skip: !isTimeline}
    );
  const timelineRows = useMemo(() => timelineData?.data ?? [], [timelineData]);
  const timelineTotal = timelineData?.totalRecords ?? 0;
  const timelineTruncated = timelineTotal > TIMELINE_PAGE_LENGTH;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      enableBottomToolbar: false,
      mantineTableBodyRowProps: ({row}) =>
        row.original.assigneeId
          ? {}
          : {
              bg: 'yellow.0',
              style: {borderLeft: '3px solid var(--mantine-color-yellow-6)'},
            },
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => openDetailFor(row.original)} />
          {isInspectionEditableState(row.original.state) && perms.update && (
            <TableRowActions.Edit
              onClick={() =>
                navigate(
                  PathUtility.InspectionsEdit(row.original.id)
                )
              }
            />
          )}
          {isInspectionEditableState(row.original.state) && perms.delete && (
            <TableRowActions.Menu>
              <TableRowActions.MenuItem
                color="red"
                onClick={() => {
                  setDeleteTarget(row.original);
                  confirmDeleteHandlers.open();
                }}>
                Delete
              </TableRowActions.MenuItem>
            </TableRowActions.Menu>
          )}
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <Textbox
              ariaLabel="Search inspections"
              placeholder="Search"
              value={searchInput}
              onChangeCallback={e => setSearchInput(e.target.value)}
              props={{leftSection: <IconSearch size={16} />, w: {base: '100%', sm: 220}}}
            />
            <SelectBox
              ariaLabel="Pass/Fail"
              placeholder="Pass/Fail"
              options={PASS_FAIL_OPTIONS}
              value={passFail}
              onSelectChangeCallback={value => setPassFail(value || 'all')}
              wrapperProps={{w: {base: '100%', sm: 160}}}
            />
            <Textbox
              ariaLabel="Filter by building"
              placeholder="Building/Location"
              value={buildingInput}
              onChangeCallback={e => setBuildingInput(e.target.value)}
              props={{w: {base: '100%', sm: 180}}}
            />
            {scheduleFilterBox}
          </TableToolbar.Filters>
          <TableToolbar.Actions>
            {perms.create && (
              <TableToolbar.Action
                variant="create"
                onClick={() =>
                  navigate(
                    PathUtility.InspectionsCreate()
                  )
                }>
                New Inspection
              </TableToolbar.Action>
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  const idOption = (o: {value: string; label: string}) => ({
    id: o.value,
    label: o.label,
  });

  const onEditRow = (row: InspectionSimpleResponse) =>
    navigate(PathUtility.InspectionsEdit(row.id));
  const onDeleteRow = (row: InspectionSimpleResponse) => {
    setDeleteTarget(row);
    confirmDeleteHandlers.open();
  };

  return (
    <Stack>
      <TableToolbar>
        <TableToolbar.Filters>
          <SegmentedControl
            value={viewMode}
            onChange={value => setViewMode(value as ManageViewMode)}
            data={VIEW_MODE_OPTIONS}
          />
        </TableToolbar.Filters>
        {canManage && (
          <TableToolbar.Actions>
            <TableToolbar.Action
              icon={<IconCalendarStats size={16} />}
              component={Link}
              to={PathUtility.InspectionsDispatch()}>
              Dispatch calendar
            </TableToolbar.Action>
          </TableToolbar.Actions>
        )}
      </TableToolbar>

      {isTimeline ? (
        <>
          <TableToolbar>
            <TableToolbar.Filters>
              <Textbox
                ariaLabel="Search inspections"
                placeholder="Search"
                value={searchInput}
                onChangeCallback={e => setSearchInput(e.target.value)}
                props={{leftSection: <IconSearch size={16} />, w: {base: '100%', sm: 200}}}
              />
              <SelectBox
                ariaLabel="Site"
                placeholder="Site"
                options={siteFilterData.map(idOption)}
                value={tlSite}
                onSelectChangeCallback={value => setTlSite(value || '')}
                wrapperProps={{w: {base: '100%', sm: 160}}}
              />
              <SelectBox
                ariaLabel="Client"
                placeholder="Client"
                options={clientFilterData.map(idOption)}
                value={tlClient}
                onSelectChangeCallback={value => setTlClient(value || '')}
                wrapperProps={{w: {base: '100%', sm: 160}}}
              />
              <SelectBox
                ariaLabel="Type"
                placeholder="Type"
                options={typeFilterData.map(idOption)}
                value={tlType}
                onSelectChangeCallback={value => setTlType(value || '')}
                wrapperProps={{w: {base: '100%', sm: 160}}}
              />
              <SelectBox
                ariaLabel="Status"
                placeholder="Status"
                options={(
                  InspectionStateDataOptions as {value: string; label: string}[]
                ).map(idOption)}
                value={tlState}
                onSelectChangeCallback={value => setTlState(value || '')}
                wrapperProps={{w: {base: '100%', sm: 150}}}
              />
              <SelectBox
                ariaLabel="Assignee"
                placeholder="Assignee"
                options={assigneeFilterData}
                value={tlAssignee}
                onSelectChangeCallback={value => setTlAssignee(value || '')}
                wrapperProps={{w: {base: '100%', sm: 170}}}
              />
              {scheduleFilterBox}
            </TableToolbar.Filters>
            <TableToolbar.Actions>
              {perms.create && (
                <TableToolbar.Action
                  variant="create"
                  onClick={() =>
                    navigate(
                      PathUtility.InspectionsCreate()
                    )
                  }>
                  New Inspection
                </TableToolbar.Action>
              )}
            </TableToolbar.Actions>
          </TableToolbar>

          {timelineTruncated && (
            <Alert color="blue" variant="light">
              Showing the first {TIMELINE_PAGE_LENGTH} of {timelineTotal}{' '}
              inspections — switch to Table view to page through all of them.
            </Alert>
          )}

          <InspectionTimelineList
            items={timelineRows}
            mode={viewMode}
            isLoading={isTimelineLoading}
            canManage={canManage}
            canEdit={perms.update}
            canDelete={perms.delete}
            onView={openDetailFor}
            onEdit={onEditRow}
            onDelete={onDeleteRow}
            onAssign={openDetailFor}
          />
        </>
      ) : (
        <>
          {table}

          <Paginator
            mt="sm"
            currentPage={pagination.pageIndex + 1}
            totalRecords={totalCount}
            perPage={pagination.pageSize}
            onChange={(_event, page) =>
              setPagination(prev => ({...prev, pageIndex: page - 1}))
            }
            captionSuffix={
              unassignedCount > 0
                ? `· ${unassignedCount} unassigned need an inspector`
                : undefined
            }
          />
        </>
      )}

      <InspectionDetailDrawer
        opened={detailOpened}
        onClose={() => {
          detailHandlers.close();
          setDetailTarget(undefined);
        }}
        inspection={detailTarget}
      />

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        confirmationButtonDisabled={isDeleting}
        title="Are you sure you want to delete this inspection?"
        body="This action cannot be undone."
      />
    </Stack>
  );
};
